import { ActivityTraceView } from '@/components/assistant-ui/activity-trace-view';
import { useTurnActivity } from '@/components/assistant-ui/activity-store';
import { useWorkspace } from './context';
import { WorkspaceIcon } from './icons';
import { useChatCopy } from '@/lib/chat-i18n';
import { activitySourceReference } from './activity-source-reference';
import { skillStepDetail } from './skill-activity';

/** Presentation only: a cited source or read object reads as its title, never a raw URL or id. */
function sourceTitle(ws: ReturnType<typeof useWorkspace>, id: string) {
  return (
    ws.data.sources.find((source) => source.id === id)?.title ??
    ws.data.files.find((file) => file.id === id)?.name ??
    ws.data.results.find((result) => result.id === id)?.title ??
    ws.data.skills.find((skill) => skill.id === id)?.draft.name ??
    null
  );
}

/** One open affordance for every row that opens something: a trailing chevron. */
const openChevron = <WorkspaceIcon name="chevron" size={14} className="ws-source-link-open" />;

export function ActivityCanvas({
  messageId,
  step,
  target,
  onTabChange,
}: {
  messageId: string;
  step?: string;
  target: { kind: 'activity'; id: string; tab?: string; step?: string };
  onTabChange?: (tab: 'steps' | 'sources') => void;
}) {
  const ws = useWorkspace();
  const copy = useChatCopy();
  const trace = useTurnActivity(ws.threadId, messageId);
  const tab = target.tab === 'sources' ? 'sources' : 'steps';
  return (
    <ActivityTraceView
      trace={trace}
      tab={tab}
      step={step}
      resolveTitle={(id) => sourceTitle(ws, id)}
      stepDetail={skillStepDetail}
      sourceContent={(referenceSource) => {
        const reference = activitySourceReference(
          referenceSource.id,
          referenceSource.href,
          typeof window === 'undefined' ? undefined : window.location.href,
        );
        if (reference.kind === 'external') {
          return (
            <a
              className="ws-source-link"
              href={reference.href}
              target="_blank"
              rel="noreferrer"
              data-canvas-trigger="true"
              data-source-href={reference.href}
              onClick={(event) => {
                // Modified clicks keep the real link (new tab, copy address).
                if (
                  event.button !== 0 ||
                  event.metaKey ||
                  event.ctrlKey ||
                  event.shiftKey ||
                  event.altKey
                )
                  return;
                event.preventDefault();
                ws.openCanvas({ kind: 'web', id: reference.href, title: reference.label });
              }}
            >
              <WorkspaceIcon name="globe" size={16} />
              <span>{reference.label}</span>
              {openChevron}
            </a>
          );
        }
        if (reference.kind === 'plain') return <span>{reference.text}</span>;
        const id = reference.kind === 'internal' ? reference.target.id : reference.id;
        const file = ws.data.files.find((value) => value.id === id);
        const source = ws.data.sources.find((value) => value.id === id);
        if (reference.kind === 'id' && !file && !source) return <span>{reference.id}</span>;
        const destination =
          reference.kind === 'internal'
            ? reference.target
            : { kind: file ? ('file' as const) : ('source' as const), id: reference.id };
        const label = (id ? sourceTitle(ws, id) : null) ?? copy.citationTitle;
        return (
          <button
            type="button"
            className="ws-source-link"
            data-canvas-trigger="true"
            data-source-href={referenceSource.href}
            onClick={() => ws.openCanvas(destination)}
          >
            <WorkspaceIcon name="knowledge" size={16} />
            <span>{label}</span>
            {openChevron}
          </button>
        );
      }}
      onTabChange={(next) =>
        onTabChange
          ? onTabChange(next as 'steps' | 'sources')
          : ws.openCanvas({ ...target, tab: next })
      }
    />
  );
}
